<template>
    <div id="graph-div" style="width: 100%; height: 500px; position: relative;">
        <vue-flow v-model="elements" :default-zoom="1" :min-zoom="0.15" :max-zoom="4" :fit-view-on-init="true"
            :node-dimensions="nodeDimensions" @nodeMouseEnter="showTooltip" @nodeMouseLeave="hideTooltip">
            <background pattern-color="#aaa" gap="16" />
            <controls />
            <mini-map />
        </vue-flow>
    </div>
</template>

<script>
import { VueFlow, Background, Controls, MiniMap } from '@vue-flow/core';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import YAML from 'yaml';

// node alignment configuration
const NODE_WIDTH = 350;
const NODE_HEIGHT = 50;
const X_GAP = 60; // Gap between sequential nodes (now horizontal)
const Y_GAP = 60; // Gap between parallel nodes (now vertical)
const CHAR_WIDTH = 10; // Approximate pixels per character
const MIN_WIDTH = 100;
const PADDING = 0; // Left + Right padding inside the box

export default {
    name: 'PipelineFlow',
    components: {
        VueFlow,
        Background,
        Controls,
        MiniMap
    },
    props: ["pipelineExecutionDetails", "pipelineJobConfig", "jobs"],
    data() {
        return {
            elements: [],
            nodeDimensions: { width: NODE_WIDTH, height: NODE_HEIGHT },
            statusColorClass: {
                'running': 'running-node',
                'succeeded': 'succeeded-node',
                'failed': 'failed-node',
                'cancelled': 'cancelled-node',
                'submitted': 'submitted-node',
                'not_started': 'waiting-node',
                'waiting': 'waiting-node' // fallback
            },

            idCounter: 0, // for unique node id generation
        };
    },
    computed: {
        pipelineConfig() {
            if (!this.pipelineJobConfig?.jobDetails?.system_files) return null;
            const configFile = this.pipelineJobConfig.jobDetails.system_files.find(
                f => f.relative_path === 'config.yaml'
            );
            const varsFile = this.pipelineJobConfig.jobDetails.system_files.find(
                f => f.relative_path === 'vars.yaml'
            );
            if (!configFile?.content) return null;
            try {
                // merging is required, since the config file may refer anchors defined in vars file.
                // yaml parser does not support a case where anchors are used but not defined.
                const mergedFile = varsFile?.content + '\n' + configFile?.content;
                const parsed = YAML.parse(mergedFile);

                // normalize root
                if (parsed.in_sequence) return { in_sequence: parsed.in_sequence };
                if (parsed.in_parallel) return { in_parallel: parsed.in_parallel };

                return null;
            } catch (e) {
                console.error('YAML parse error', e);
                return null;
            }
        }
    },
    mounted() {
        if (this.pipelineConfig) {
            this.generateFlow();
        }
    },
    methods: {
        nextId(name = 'job') {
            return `${name}-${++this.idCounter}`;
        },
        getJobKind(jobName) {
            const job = this.jobs.find(job => job.jobDetails?.name === jobName);
            return job?.jobDetails?.kind || "N/A";
        },
        showTooltip(event) {
            const { id } = event.node;
            const mouseEvent = event.event;
            const node = this.elements.find(el => el.id === id && !el.source);

            if (!node) return;

            const tooltip = document.createElement('div');
            tooltip.innerHTML = `
                <div style='font-size: 12px; padding: 3px;'>
                    <strong>Name: </strong> ${node.data.name} <br>
                    <strong>Kind: </strong> ${node.data.kind || 'N/A'} <br>
                    <strong>Status: </strong> ${node.data.status.toUpperCase() || 'N/A'}
                </div>
            `;

            tooltip.style.left = mouseEvent.pageX + 5 + 'px';
            tooltip.style.top = mouseEvent.pageY + 5 + 'px';
            tooltip.classList.add('vueflow-tooltip');

            this.hideTooltip(); // Clear old ones
            document.body.appendChild(tooltip);
        },
        hideTooltip() {
            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) {
                existingTooltip.remove();
            }
        },
        // Calculate node width based on label
        calculateNodeWidth(label) {
            const textWidth = label.length * CHAR_WIDTH;
            return Math.max(MIN_WIDTH, textWidth + PADDING);
        },

        generateFlow() {
            const layoutTree = this.getDimensions(this.pipelineConfig);
            // We pass the layoutTree's own height as the 'containerHeight' 
            // to ensure the very first block is centered if it's a sequence
            const { nodes, edges } = this.buildFlow(layoutTree, 0, 0, layoutTree.width, layoutTree.height);
            this.elements = [...nodes, ...edges];
        },

        getDimensions(node) {
            if (node.job_name) {
                const dynamicWidth = this.calculateNodeWidth(node.job_name);
                return {
                    ...node,
                    width: dynamicWidth,
                    height: 50 // Matching your NODE_HEIGHT
                };
            }

            if (node.in_sequence) {
                const children = node.in_sequence.map(child => this.getDimensions(child));
                return {
                    in_sequence: children,
                    // Width is sum of children + gaps
                    width: children.reduce((sum, c) => sum + c.width, 0) + (children.length - 1) * X_GAP,
                    // Height is the max height of any child in the sequence
                    height: Math.max(...children.map(c => c.height))
                };
            }

            if (node.in_parallel) {
                const children = node.in_parallel.map(child => this.getDimensions(child));
                return {
                    in_parallel: children,
                    // Width is the widest child
                    width: Math.max(...children.map(c => c.width)),
                    // Height is sum of children + gaps
                    height: children.reduce((sum, c) => sum + c.height, 0) + (children.length - 1) * Y_GAP
                };
            }
        },

        /**
         * @param node The layout node
         * @param startX Current X position
         * @param startY Current Y position
         * @param containerW The width of the "cell" this node lives in
         * @param containerH The height of the "cell" this node lives in
         */
        buildFlow(node, startX, startY, containerW, containerH) {
            const xOffset = (containerW - node.width) / 2;
            const yOffset = (containerH - node.height) / 2;

            if (node.job_name) {
                const id = this.nextId(node.job_name);
                let status = "WAITING";
                if (this.pipelineExecutionDetails?.executionDetails) {
                    status = this.pipelineExecutionDetails?.executionDetails[node.job_name]?.status || "WAITING";
                }

                return {
                    nodes: [{
                        id,
                        type: 'default', // Changed from 'custom' to 'default'
                        position: { x: startX + xOffset, y: startY + yOffset },
                        label: node.job_name, // Vue Flow uses this for the text display
                        sourcePosition: 'right', // Forces handle to Right
                        targetPosition: 'left',  // Forces handle to Left
                        style: {
                            width: `${node.width}px`,
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        },
                        // Applying the status class directly to the node
                        class: this.statusColorClass[status.toLowerCase()] || 'waiting-node',
                        data: {
                            name: node.job_name,
                            kind: this.getJobKind(node.job_name),
                            status: status
                        }
                    }],
                    edges: [],
                    entryIds: [id],
                    exitIds: [id]
                };
            }

            if (node.in_sequence) {
                let currentX = startX;
                let nodes = [];
                let edges = [];
                let entryIds = [];
                let prevExitIds = [];

                node.in_sequence.forEach((child, index) => {
                    const result = this.buildFlow(child, currentX, startY, child.width, containerH);
                    nodes.push(...result.nodes);
                    edges.push(...result.edges);
                    if (index === 0) entryIds = result.entryIds;

                    prevExitIds.forEach(src =>
                        result.entryIds.forEach(tgt =>
                            edges.push({
                                id: `e-${src}-${tgt}`,
                                source: src,
                                target: tgt,
                                type: 'smoothstep',
                                animated: true,
                                // This ensures the line connects Right End to Left End
                                style: { stroke: '#000000', strokeWidth: 2 }
                            })
                        )
                    );
                    prevExitIds = result.exitIds;
                    currentX += child.width + X_GAP;
                });
                return { nodes, edges, entryIds, exitIds: prevExitIds };
            }
            // ... rest of in_parallel logic
            if (node.in_parallel) {
                let currentY = startY;
                let nodes = [];
                let edges = [];
                let entryIds = [];
                let exitIds = [];

                node.in_parallel.forEach(child => {
                    // Each child in parallel is given the FULL width of the parallel 
                    // block so they can center themselves horizontally
                    const result = this.buildFlow(child, startX, currentY, containerW, child.height);

                    nodes.push(...result.nodes);
                    edges.push(...result.edges);
                    entryIds.push(...result.entryIds);
                    exitIds.push(...result.exitIds);
                    currentY += child.height + Y_GAP;
                });
                return { nodes, edges, entryIds, exitIds };
            }
        }
    },
    watch: {
        pipelineExecutionDetails: {
            handler(newVal) {
                if (newVal && this.pipelineConfig) {
                    this.idCounter = 0;
                    this.generateFlow();
                }
            }
        }
    }
};
</script>

<style>
.vue-flow {
    width: 100%;
    height: 100%;
}

.vue-flow__node {
    cursor: pointer;
}

.vue-flow__edge path {
    stroke: #ccc;
    stroke-width: 2px;
}

.vueflow-tooltip {
    position: absolute;
    pointer-events: none;
    border-radius: 4px;
    z-index: 1000;
    background-color: #6E84A3;
    color: #fff;
    visibility: visible !important;
}

/* Remove or comment out .custom-node */
.vue-flow__handle {
    opacity: 0 !important;
    /* Optional: Ensure they don't take up space if needed, 
       though opacity 0 is usually enough to 'hide' them */
    background: transparent !important;
    border: none !important;
}

.vue-flow__node-default {
    border-radius: 5px;
    border: 1px solid #000000;
    color: #000000;
    font-size: 14px;
    font-weight: 500;
}

/* Update your status classes to apply to the default node background */
.vue-flow__node-default.running-node {
    background-color: #2C7BE5 !important;
    color: #ffffff !important;
    border: none !important;

    /* IMPORTANT: do NOT change positioning */
    overflow: hidden;
    animation: runningGlow 1.6s ease-in-out infinite;
}

/* Stripe overlay */
.vue-flow__node-default.running-node::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    pointer-events: none;

    background-image: repeating-linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.25) 0px,
        rgba(255, 255, 255, 0.25) 6px,
        rgba(255, 255, 255, 0.05) 6px,
        rgba(255, 255, 255, 0.05) 16px
    );

    background-size: 68px 68px;
    animation: diagonalFlow 9s linear infinite;
    opacity: 0.35;
}


/* Glow pulse */
@keyframes runningGlow {
    0% {
        box-shadow:
            0 0 0 0 rgba(255, 215, 0, 0.7),
            0 0 6px 2px rgba(255, 215, 0, 0.5);
    }

    50% {
        box-shadow:
            0 0 12px 6px rgba(255, 215, 0, 1.0),
            0 0 24px 10px rgba(255, 215, 0, 0.6);
    }

    100% {
        box-shadow:
            0 0 0 0 rgba(255, 215, 0, 0.7),
            0 0 6px 2px rgba(255, 215, 0, 0.5);
    }
}

@keyframes diagonalFlow {
    from { background-position: 0px 0px; }
    to { background-position: 68px 68px; }
}

.vue-flow__node-default.succeeded-node {
    background-color: #00D97E !important;
    border: none !important;
}

.vue-flow__node-default.failed-node {
    background-color: #e63757 !important;
    color: white !important;
    border: none !important;
}

.vue-flow__node-default.cancelled-node {
    background-color: #acb4c1 !important;
    border: none !important;
}

.vue-flow__node-default.submitted-node {
    background-color: #ffd118 !important;
    border: none !important;
}

.vue-flow__node-default.waiting-node {
    background-color: #EDF2F9 !important;
}

/* Ensure edges are visible and dark */
.vue-flow__edge-path {
    stroke: #000000 !important;
    stroke-width: 2px;
}
</style>